import API from "../helpers/API";
import { REACT_APP_API_URL } from "../helpers/constants";

class BackendService {
  constructor(api = null) {
    this.api = api || new API();
  }

  verifyToken(token) {
    return this.api.get(`${REACT_APP_API_URL}/auth/verify_token/${token}`);
  }

  // GET
  getDeviceIds() {
    return this.api.get(`${REACT_APP_API_URL}/device_ids/`);
  }

  getDSAProjects() {
    return this.api.get(`${REACT_APP_API_URL}/dsa/projects`);
  }

  getExtractedAudiences() {
    return this.api.get(`${REACT_APP_API_URL}/audience_exports/`);
  }

  getProjects() {
    return this.api.get(`${REACT_APP_API_URL}/projects`);
  }

  getRecentFiles(count) {
    return this.api.get(`${REACT_APP_API_URL}/files/recent?count=${count}`);
  }

  getReports(count) {
    return this.api.get(`${REACT_APP_API_URL}/reports/recent?count=${count}`);
  }

  getRecentProjects(count) {
    return this.api.get(`${REACT_APP_API_URL}/projects/recent?count=${count}`);
  }

  getCoordinates(postalCode) {
    return this.api.get(
      `${REACT_APP_API_URL}/files/coordinates?postal_code=${postalCode}`
    );
  }

  getProjectFilesByType(marketFile = null) {
    return this.api.get(
      `${REACT_APP_API_URL}/files/project_files_by_type?market_file=${marketFile}`
    );
  }

  getSegmentsData(fileId) {
    return this.api.get(`${REACT_APP_API_URL}/files/${fileId}/attributes`);
  }

  getAudiencePostalCodes(extractId) {
    return this.api.get(
      `${REACT_APP_API_URL}/audience_exports/${extractId}/postal_codes`
    );
  }

  // PUT
  convertFileUsingNumerisProjections(fileId, useNumerisMarketProjections) {
    return this.api.put(
      `${REACT_APP_API_URL}/files/${fileId}/convert_using_numeris_projections?use_market_projections=${useNumerisMarketProjections}`
    );
  }

  // POST
  generateDataForFile(fileId) {
    return this.api.post(`${REACT_APP_API_URL}/reports/gen_data/${fileId}`);
  }

  async createProject(projectName) {
    return this.api.post(`${REACT_APP_API_URL}/projects`, {
      project_name: projectName,
    });
  }

  async updateProject(projectId, projectName) {
    return this.api.put(`${REACT_APP_API_URL}/projects/${projectId}`, {
      project_id: projectId,
      project_name: projectName,
    });
  }

  async updateProjectAssociatedFiles(projectId, file_ids) {
    return this.api.put(
      `${REACT_APP_API_URL}/projects/${projectId}/associate_files`,
      {
        file_ids,
      }
    );
  }

  submitProject(formData) {
    return this.api.post(`${REACT_APP_API_URL}/files/upload_csv`, formData);
  }

  createFile(data) {
    return this.api.post(`${REACT_APP_API_URL}/files/create_file`, data);
  }

  createMapRadiusFile(data) {
    return this.api.post(`${REACT_APP_API_URL}/files/map_radius`, data);
  }

  createMergedFile(data) {
    return this.api.post(`${REACT_APP_API_URL}/files/merge_files`, data);
  }

  createDSAProject(projectName) {
    return this.api.post(`${REACT_APP_API_URL}/dsa/projects`, {
      name: projectName,
    });
  }

  createDSAFile(data) {
    return this.api.post(`${REACT_APP_API_URL}/dsa/files`, data);
  }

  createExtract(data) {
    return this.api.post(
      `${REACT_APP_API_URL}/audience_exports/create_extract`,
      data
    );
  }

  getCarrierWalkList() {
    return this.api.get(`${REACT_APP_API_URL}/carrier_walk/reports`);
  }

  getCarrierWalksReportDetails(carrierWalkReportId) {
    return this.api.get(
      `${REACT_APP_API_URL}/carrier_walk/reports/${carrierWalkReportId}`
    );
  }

  getCarrierWalkReportCurrentVersion() {
    return this.api.get(
      `${REACT_APP_API_URL}/carrier_walk/reports/current_version`
    );
  }

  async getActivationSummaryStats(file_id, controller) {
    return this.api.get(
      `${REACT_APP_API_URL}/files/${file_id}/activation_stats`,
      {},
      controller.signal
    );
  }

  async fetchProjectsAndFilesList() {
    try {
      const [mf, cf] = await Promise.all([
        this.api.get(
          `${REACT_APP_API_URL}/files/project_files_by_type?market_file=false`
        ),
        this.api.get(
          `${REACT_APP_API_URL}/files/project_files_by_type?market_file=true`
        ),
      ]);
      return {
        data: [...mf, ...cf],
      };
    } catch (err) {
      return { error: err.message };
    }
  }

  async fetchProjects() {
    try {
      return {
        data: await this.api.get(`${REACT_APP_API_URL}/projects`),
      };
    } catch (err) {
      return { error: err.message };
    }
  }

  async fetchReportsForFile(file_id) {
    return this.api.get(
      `${REACT_APP_API_URL}/reports/files/${file_id}/reports`
    );
  }

  async updateFile(data) {
    return this.api.put(
      `${REACT_APP_API_URL}/files/${data.file_id}/update_metadata`,
      data
    );
  }

  async fetchReportsList(type = "insights") {
    return type === "insights"
      ? this.api.get(`${REACT_APP_API_URL}/reports`)
      : this.api.get(`${REACT_APP_API_URL}/report_comparison`);
  }

  async updateReport(data) {
    return this.api.put(
      `${REACT_APP_API_URL}/report_comparison/${data.id}`,
      data
    );
  }

  async getReportShareTokens(report_id) {
    return this.api.get(`${REACT_APP_API_URL}/reports/${report_id}/share`);
  }

  async shareReport(report_id, views, description) {
    return this.api.post(`${REACT_APP_API_URL}/reports/${report_id}/share`, {
      views: views,
      description: description,
    });
  }

  async deleteShareToken(report_id, tok_id) {
    return this.api.delete(`${REACT_APP_API_URL}/reports/${report_id}/share`, {
      tok_id,
    });
  }

  async fetchFileInfo(file_id) {
    return this.api.get(`${REACT_APP_API_URL}/files/${file_id}`);
  }

  async fetchAttributesForFSA(fsa) {
    return this.api.get(`${REACT_APP_API_URL}/fsa/${fsa}/attributes`);
  }

  async fetchAdSpotCredentials() {
    return this.api.get(`${REACT_APP_API_URL}/adspot`);
  }

  async createFromPostalCodes(data, retry = 0) {
    const res = await this.api.post(
      `${REACT_APP_API_URL}/files/create_from_postal_codes`,
      data
    );
    if (res.error) {
      return (await (retry < 5 && res.error !== "No valid postal codes found."))
        ? this.createFromPostalCodes(data, retry + 1)
        : res;
    }
    return res;
  }
}

export default BackendService;
